import styled from 'styled-components'
import HumanIcon from '../asset/HumanIcon'
import BlockIcon from '../asset/BlockIocn'
import UnionUp from '../asset/UnionUp'
import UnionDown from '../asset/UnionDown'
import { ISeasonalNum } from '../util/algorithm/algoInterface'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px #00000029;
`

const Head = styled.div`
  display: flex;
  flex-direction: column;
  background: #373a40;
  gap: 8px;
  border-radius: 0px 0px 4px 4px;
`

const StatusBoardWrapper = styled.div`
  display: flex;
  padding: 16px 24px;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 0px 0px 4px 4px;
  border-top: 1px solid var(--gray60, #808080);
  background: var(--bluegray100, #373a40);
`

const StatusBoardDetail = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
`

const IconTextFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`

const IconTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const TextDetail = styled.div<TextProps>`
  color: ${(props) => (props.fontColor ? props.fontColor : '#ffffff')};
  font-family: SUIT;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '16px')};
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

const PriceTableFrame = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const ChargeUnitTableBody = styled(TableBody)`
  margin-top: 14px;
`

const TableRowListWrapper = styled.div`
  display: flex;
  align-items: center;
`

const TotalLabelRowListWrapper = styled(TableRowListWrapper)`
  margin-top: 6px;
`

const TotalIncomeRowListWrapper = styled(TableRowListWrapper)`
  margin-top: 0px;
`

const RowDetail = styled.div<TableRowProps>`
  display: flex;
  width: 64px;
  padding: 4px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  background: ${(props) =>
    props.backgroundColor ? props.backgroundColor : '#4d5055'};
  ${(props) => props.borderBottom && `border-bottom: ${props.borderBottom};`}
  min-width: 50px;
`
const IncomeText = styled.div<TextProps>`
  color: ${(props) => (props.fontColor ? props.fontColor : '#e6e6e6')};
  font-family: SUIT;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '12px')};
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: center;
  text-decoration: ${(props) =>
    props.textDecoration ? props.textDecoration : 'line-through'};
`

const IncomeColumnHeader = styled(RowDetail)`
  width: 116px;
  min-width: 116px;
`

const LastIncomeColumnHeader = styled(IncomeColumnHeader)`
  width: 136px;
  min-width: 136px;
`

const IncomeHeaderWithUnit = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 18px;
`

const IncomeHeaderLabel = styled(TextDetail)`
  white-space: nowrap;
`

const IncomeUnitText = styled(TextDetail)`
  font-weight: 500;
  white-space: nowrap;
`

const IncomeColumnCell = styled(RowDetail)`
  width: 116px;
  min-width: 116px;
`

const LastIncomeColumnCell = styled(IncomeColumnCell)`
  box-sizing: border-box;
  width: 136px;
  min-width: 136px;
  align-items: flex-end;
  padding: 4px 12px 4px 0px;
`

const BenefitFrame = styled.div`
  display: inline-flex;
  position: relative;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`

const BenefitIconWrapper = styled.span`
  display: inline-flex;
  width: 10px;
  height: 10px;
  align-items: center;
  justify-content: center;
`

const BenefitText = styled(IncomeText)`
  text-decoration: none;
  white-space: nowrap;
`

const BenefitKoreanAmountText = styled(BenefitText)`
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  opacity: 0.68;
`

interface TextProps {
  fontSize?: string
  fontColor?: string
  textDecoration?: string
}

interface TableRowProps {
  backgroundColor?: string
  borderBottom?: string
  borderRadius?: string
}

interface Props {
  name?: string
  ratePlan?: string
  chartUnitResult?: any
  beforeIncome?: ISeasonalNum
  afterIncome?: ISeasonalNum
  recIncome?: ISeasonalNum
}

type SeasonKey = keyof ISeasonalNum

const seasons: { key: SeasonKey; label: string }[] = [
  { key: 'spr', label: '봄' },
  { key: 'sum', label: '여름' },
  { key: 'fall', label: '가을' },
  { key: 'win', label: '겨울' },
]

const isValidNumber = (value?: number) =>
  typeof value === 'number' && Number.isFinite(value)

const formatNumber = (value?: number, fallback = '-') => {
  const numberValue = Number(value)

  if (!Number.isFinite(numberValue)) {
    return fallback
  }

  return Number(numberValue.toFixed(0)).toLocaleString()
}

const formatKoreanManwon = (value?: number) => {
  const numericValue = Number(value)
  const manwonValue = Math.round(Math.abs(numericValue) / 10000)

  if (!Number.isFinite(numericValue)) {
    return undefined
  }

  const eokValue = Math.floor(manwonValue / 10000)
  const remainingManwon = manwonValue % 10000

  if (eokValue > 0 && remainingManwon > 0) {
    return `${eokValue.toLocaleString()}억 ${remainingManwon.toLocaleString()}만원`
  }

  if (eokValue > 0) {
    return `${eokValue.toLocaleString()}억원`
  }

  return `${manwonValue.toLocaleString()}만원`
}

const hasSeasonalValue = (value?: ISeasonalNum) =>
  value
    ? seasons.some(({ key }) => isValidNumber(value[key]) && value[key] !== 0)
    : false

const hasChargeUnitValue = (value?: any) =>
  seasons.some(({ key }) =>
    ['min', 'mid', 'max'].some(
      (load) => isValidNumber(value?.[key]?.[load]) && value[key][load] !== 0
    )
  )

const sumSeasonalValue = (value?: ISeasonalNum) =>
  value
    ? seasons.reduce((total, { key }) => total + (value[key] ?? 0), 0)
    : undefined

const getBenefitValue = (
  beforeValue?: number,
  afterValue?: number,
  recValue?: number
) => {
  const beforeNumber = Number(beforeValue)
  const afterNumber = Number(afterValue)
  const recNumber = Number(recValue ?? 0)

  if (!Number.isFinite(beforeNumber) || !Number.isFinite(afterNumber)) {
    return undefined
  }

  return Number((afterNumber + recNumber - beforeNumber).toFixed(0))
}

export const PowerPurchaseBoard = (props: Props) => {
  const {
    name,
    ratePlan,
    chartUnitResult,
    beforeIncome,
    afterIncome,
    recIncome,
  } = props
  const isChargeUnitCalculated = hasChargeUnitValue(chartUnitResult)
  const isIncomeCalculated =
    hasSeasonalValue(beforeIncome) || hasSeasonalValue(afterIncome)
  const showRecIncome = recIncome !== undefined

  const getWidth = () => {
    const element = document.getElementById('income-header')
    const width = element?.offsetWidth
    return width
  }
  const maxWidth = getWidth()
  const beforeTotal = sumSeasonalValue(beforeIncome)
  const afterTotal = sumSeasonalValue(afterIncome)
  const recTotal = sumSeasonalValue(recIncome)
  const changedTotalNumber = getBenefitValue(beforeTotal, afterTotal, recTotal)
  const formatChargeUnitNumber = (value?: number) =>
    isChargeUnitCalculated ? formatNumber(value) : '-'
  const formatIncomeNumber = (value?: number) =>
    isIncomeCalculated ? formatNumber(value) : '-'
  const renderBenefit = (value?: number, showKoreanAmount = false) => {
    if (!isIncomeCalculated || !isValidNumber(value)) {
      return (
        <BenefitText fontColor="#E6E6E6" fontSize="12px" textDecoration="none">
          -
        </BenefitText>
      )
    }

    const numericValue = Number(value)
    const koreanAmountText = showKoreanAmount
      ? formatKoreanManwon(numericValue)
      : undefined
    const fontColor =
      numericValue > 0 ? '#649bf3' : numericValue < 0 ? '#F36464' : '#E6E6E6'
    const icon =
      numericValue > 0 ? (
        <UnionUp color={fontColor} />
      ) : numericValue < 0 ? (
        <UnionDown color={fontColor} />
      ) : null

    return (
      <BenefitFrame>
        {icon && <BenefitIconWrapper>{icon}</BenefitIconWrapper>}
        <BenefitText
          fontColor={fontColor}
          fontSize="12px"
          textDecoration="none"
        >
          {formatNumber(Math.abs(numericValue))}
        </BenefitText>
        {koreanAmountText ? (
          <BenefitKoreanAmountText fontColor={fontColor} textDecoration="none">
            {koreanAmountText}
          </BenefitKoreanAmountText>
        ) : null}
      </BenefitFrame>
    )
  }

  const renderIncomeRow = (seasonKey?: SeasonKey) => {
    const beforeValue = seasonKey ? beforeIncome?.[seasonKey] : beforeTotal
    const afterValue = seasonKey ? afterIncome?.[seasonKey] : afterTotal
    const recValue = seasonKey ? recIncome?.[seasonKey] : recTotal
    const benefitValue = getBenefitValue(beforeValue, afterValue, recValue)

    return (
      <TableRowListWrapper>
        <IncomeColumnCell backgroundColor="#373a40">
          <IncomeText fontSize="12px" fontColor="#E6E6E6" textDecoration="none">
            {formatIncomeNumber(beforeValue)}
          </IncomeText>
        </IncomeColumnCell>
        <IncomeColumnCell backgroundColor="#373a40">
          <IncomeText fontColor="#fff" fontSize="12px" textDecoration="none">
            {formatIncomeNumber(afterValue)}
          </IncomeText>
        </IncomeColumnCell>
        {showRecIncome ? (
          <IncomeColumnCell backgroundColor="#373a40">
            <IncomeText
              fontSize="12px"
              fontColor="#E6E6E6"
              textDecoration="none"
            >
              {formatIncomeNumber(recValue)}
            </IncomeText>
          </IncomeColumnCell>
        ) : null}
        <LastIncomeColumnCell backgroundColor="#373a40">
          {renderBenefit(benefitValue)}
        </LastIncomeColumnCell>
      </TableRowListWrapper>
    )
  }

  return (
    <Wrapper>
      <Head>
        <StatusBoardWrapper>
          <StatusBoardDetail>
            <IconTextFrame>
              <IconTextWrapper>
                <HumanIcon />
                <TextDetail> {name || '- - -'}</TextDetail>
                <TextDetail>PV 발전사업자</TextDetail>
              </IconTextWrapper>
              <IconTextWrapper>
                <BlockIcon />
                <TextDetail> {ratePlan || '- - -'}</TextDetail>
              </IconTextWrapper>
            </IconTextFrame>
            <PriceTableFrame>
              <ChargeUnitTableBody>
                <TableRowListWrapper>
                  <RowDetail
                    style={{
                      borderTopLeftRadius: '4px',
                      borderBottomLeftRadius: '4px',
                    }}
                  >
                    <TextDetail fontSize="10px" fontColor="#E6E6E6">
                      원/kWh
                    </TextDetail>
                  </RowDetail>
                  <RowDetail>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      경부하
                    </TextDetail>
                  </RowDetail>
                  <RowDetail>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      중간부하
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    style={{
                      borderTopRightRadius: '4px',
                      borderBottomRightRadius: '4px',
                    }}
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      최대부하
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TableRowListWrapper>
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      봄
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.spr?.min)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.spr?.mid)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.spr?.max)}
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TableRowListWrapper>
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      여름
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.sum?.min)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.sum?.mid)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.sum?.max)}
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TableRowListWrapper>
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      가을
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.fall?.min)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.fall?.mid)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.fall?.max)}
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TableRowListWrapper>
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      겨울
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.win?.min)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.win?.mid)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.win?.max)}
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TotalLabelRowListWrapper>
                  <RowDetail style={{ visibility: 'hidden' }} />
                  <RowDetail style={{ visibility: 'hidden' }} />
                  <RowDetail style={{ visibility: 'hidden' }} />
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      총합
                    </TextDetail>
                  </RowDetail>
                </TotalLabelRowListWrapper>
              </ChargeUnitTableBody>
              <TableBody>
                <TableRowListWrapper>
                  <IncomeColumnHeader
                    id="income-header"
                    style={{
                      borderTopLeftRadius: '4px',
                      borderBottomLeftRadius: '4px',
                      width: maxWidth ? `${maxWidth}px` : undefined,
                    }}
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      PV 발전사업자
                      <br />
                      연간 기존 수익
                    </TextDetail>
                  </IncomeColumnHeader>
                  <IncomeColumnHeader>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {showRecIncome ? (
                        <>
                          PV 발전사업자
                          <br />
                          연간 전력량 예상 수익
                        </>
                      ) : (
                        <>
                          PV 발전사업자
                          <br />
                          연간 예상 수익
                        </>
                      )}
                    </TextDetail>
                  </IncomeColumnHeader>
                  {showRecIncome ? (
                    <IncomeColumnHeader>
                      <TextDetail fontSize="12px" fontColor="#E6E6E6">
                        PV 발전사업자
                        <br />
                        연간 REC 예상 수익
                      </TextDetail>
                    </IncomeColumnHeader>
                  ) : null}
                  <LastIncomeColumnHeader
                    style={{
                      borderTopRightRadius: '4px',
                      borderBottomRightRadius: '4px',
                    }}
                  >
                    <IncomeHeaderWithUnit>
                      <IncomeHeaderLabel fontSize="12px" fontColor="#E6E6E6">
                        PV 발전사업자
                        <br />
                        연간 수익 증감액
                      </IncomeHeaderLabel>
                      <IncomeUnitText fontSize="12px" fontColor="#E6E6E6">
                        [원]
                      </IncomeUnitText>
                    </IncomeHeaderWithUnit>
                  </LastIncomeColumnHeader>
                </TableRowListWrapper>
                {renderIncomeRow('spr')}
                {renderIncomeRow('sum')}
                {renderIncomeRow('fall')}
                {renderIncomeRow('win')}
                <TotalIncomeRowListWrapper>
                  <IncomeColumnCell backgroundColor="#373a40">
                    <IncomeText
                      fontSize="12px"
                      fontColor="#E6E6E6"
                      textDecoration="none"
                    >
                      {formatIncomeNumber(beforeTotal)}
                    </IncomeText>
                  </IncomeColumnCell>
                  <IncomeColumnCell backgroundColor="#373a40">
                    <IncomeText
                      fontColor="#fff"
                      fontSize="12px"
                      textDecoration="none"
                    >
                      {formatIncomeNumber(afterTotal)}
                    </IncomeText>
                  </IncomeColumnCell>
                  {showRecIncome ? (
                    <IncomeColumnCell backgroundColor="#373a40">
                      <IncomeText
                        fontSize="12px"
                        fontColor="#E6E6E6"
                        textDecoration="none"
                      >
                        {formatIncomeNumber(recTotal)}
                      </IncomeText>
                    </IncomeColumnCell>
                  ) : null}
                  <LastIncomeColumnCell backgroundColor="#373a40">
                    {renderBenefit(changedTotalNumber, true)}
                  </LastIncomeColumnCell>
                </TotalIncomeRowListWrapper>
              </TableBody>
            </PriceTableFrame>
          </StatusBoardDetail>
        </StatusBoardWrapper>
      </Head>
    </Wrapper>
  )
}
